import { dbAdd, dbGetAll, dbGetById, dbPut } from '../indexedDb.ts';

export interface LocalInvestmentAllocation {
  id: number;
  risk_profile: string;
  asset_classes: string[];
  target_allocations: Record<string, number>;
  current_allocations: Record<string, number>;
  last_reviewed_date?: string;
  created_at: string;
  updated_at?: string;
  server_id?: number;
  last_synced_at?: string;
  needs_sync?: boolean;
}

export function listInvestmentAllocations() {
  return dbGetAll<LocalInvestmentAllocation>('investment_allocations');
}

export function getInvestmentAllocationById(id: number) {
  return dbGetById<LocalInvestmentAllocation>('investment_allocations', id);
}

export async function getLatestAllocation() {
  const all = await listInvestmentAllocations();
  if (all.length === 0) return undefined;
  return all[all.length - 1];
}

export async function getInvestmentAllocationByServerId(serverId: number) {
  const all = await listInvestmentAllocations();
  return all.find((allocation) => allocation.server_id === serverId);
}

export async function createInvestmentAllocation(
  data: Omit<LocalInvestmentAllocation, 'id' | 'created_at' | 'updated_at' | 'server_id' | 'last_synced_at' | 'needs_sync'>
) {
  const now = new Date().toISOString();
  return dbAdd('investment_allocations', { ...data, created_at: now, needs_sync: true } as any);
}

export async function updateInvestmentAllocation(id: number, data: Partial<LocalInvestmentAllocation>) {
  const current = await dbGetById<LocalInvestmentAllocation>('investment_allocations', id);
  if (!current) throw new Error('Allocation not found');
  const updated = {
    ...current,
    ...data,
    id,
    updated_at: data.updated_at ?? new Date().toISOString(),
    needs_sync: data.needs_sync ?? true,
  };
  await dbPut('investment_allocations', updated);
  return updated;
}

export async function markInvestmentAllocationSynced(id: number, serverId: number) {
  const current = await dbGetById<LocalInvestmentAllocation>('investment_allocations', id);
  if (!current) throw new Error('Allocation not found');
  current.server_id = serverId;
  current.last_synced_at = new Date().toISOString();
  current.needs_sync = false;
  await dbPut('investment_allocations', current);
  return current;
}
